/**
 * ChatWidget — floating AI assistant widget.
 *
 * Features:
 *  - Floating brand-800 button (fixed bottom-right, z-50) with pulse animation
 *  - Click opens chat panel (350px wide / 500px tall max), slides up
 *  - Header with Bot icon + title + close X
 *  - Scrollable messages area
 *  - Suggested questions fetched from /api/ai/suggestions (shown initially as chips)
 *  - Input + Send button
 *  - "Powered by YMS COE AI" footer
 *  - POST /api/ai/chat with { message, sessionId, history }
 *  - sessionId persisted in localStorage `yms_chat_session`
 *  - history = last 10 messages from current conversation
 *  - Typing indicator while waiting
 *  - Basic markdown-ish rendering (split on newlines → paragraphs)
 *  - "Sources" badges under each AI response (KB articles used)
 *  - 👍/👎 feedback buttons → POST /api/ai/feedback
 *  - Auto-scroll to bottom on new message
 *  - Welcome message on first open
 *  - 429 rate limit → friendly message
 *  - User bubble: brand-800 right-aligned; AI bubble: white/ink-100 left-aligned
 *  - Mobile responsive (full width on small screens)
 *  - Conversation persisted in localStorage `yms_chat_history`
 */
import { useState, useRef, useEffect, useCallback } from "react"
import { Bot, MessageCircle, X, Send, Sparkles, Loader2, ThumbsUp, ThumbsDown, BookOpen } from "lucide-react"
import { api, ApiError } from "../../lib/api"
import { cn } from "../../lib/utils"
import { SCHOOL } from "../../lib/constants"

const SESSION_KEY = "yms_chat_session"
const HISTORY_KEY = "yms_chat_history"
const WELCOME_MESSAGE =
  "Hello! I'm the YMS COE AI Assistant. How can I help you today? Ask me about admissions, programmes, fees, campus life, and more."

interface ChatSource {
  id?: string
  title?: string
  category?: string
  url?: string
}

interface ChatMessage {
  role: "user" | "assistant"
  content: string
  sources?: ChatSource[]
  feedback?: "positive" | "negative" | null
  error?: boolean
  /** transient flag for messages that should not be persisted (e.g. rate-limit notice) */
  transient?: boolean
}

interface SuggestionResponse {
  suggestions?: string[]
}

interface ChatResponse {
  response?: string
  message?: string
  answer?: string
  reply?: string
  text?: string
  sources?: ChatSource[]
  source?: ChatSource[]
}

interface FeedbackResponse {
  ok?: boolean
  success?: boolean
}

function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY)
    if (!id) {
      id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
      localStorage.setItem(SESSION_KEY, id)
    }
    return id
  } catch {
    return `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
  }
}

function loadHistory(): ChatMessage[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) return parsed as ChatMessage[]
    return []
  } catch {
    return []
  }
}

function saveHistory(messages: ChatMessage[]) {
  try {
    // Only persist non-transient messages
    const persistable = messages.filter(m => !m.transient)
    // Keep the most recent 50 to avoid unbounded growth
    const slice = persistable.slice(-50)
    localStorage.setItem(HISTORY_KEY, JSON.stringify(slice))
  } catch {
    /* ignore quota errors */
  }
}

function extractText(res: ChatResponse): string {
  return res.response || res.message || res.answer || res.reply || res.text || ""
}

function extractSources(res: ChatResponse): ChatSource[] {
  if (Array.isArray(res.sources)) return res.sources
  if (Array.isArray(res.source)) return res.source
  return []
}

/** Render markdown-ish content: split on double-newlines into paragraphs, then on single newlines as <br/>. */
function renderContent(text: string) {
  const paras = text.split(/\n{2,}/)
  return paras.map((p, i) => {
    const lines = p.split(/\n/)
    return (
      <p key={i} className={i > 0 ? "mt-2" : ""}>
        {lines.map((line, j) => (
          <span key={j}>
            {line}
            {j < lines.length - 1 && <br />}
          </span>
        ))}
      </p>
    )
  })
}

export function ChatWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const sessionIdRef = useRef<string>("")

  // Initialize session + history on mount
  useEffect(() => {
    sessionIdRef.current = getSessionId()
    const stored = loadHistory()
    if (stored.length > 0) {
      setMessages(stored)
    }
  }, [])

  // Persist messages whenever they change
  useEffect(() => {
    saveHistory(messages)
  }, [messages])

  // Auto-scroll to bottom on new message / typing
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" })
    }
  }, [messages, loading])

  // Fetch suggestions when panel first opened
  const fetchSuggestions = useCallback(async () => {
    if (suggestionsLoaded) return
    setSuggestionsLoaded(true)
    try {
      const data = await api.get<SuggestionResponse>("/api/ai/suggestions")
      const list = Array.isArray(data?.suggestions) ? data.suggestions : []
      setSuggestions(list.slice(0, 6))
    } catch {
      // non-fatal
      setSuggestions([])
    }
  }, [suggestionsLoaded])

  useEffect(() => {
    if (open) {
      fetchSuggestions()
      // Insert welcome message on first open if empty
      if (messages.length === 0) {
        setMessages([
          { role: "assistant", content: WELCOME_MESSAGE, sources: [], feedback: null },
        ])
      }
      // Focus input shortly after open
      const t = setTimeout(() => inputRef.current?.focus(), 200)
      return () => clearTimeout(t)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  async function sendMessage(text?: string) {
    const message = (text ?? input).trim()
    if (!message || loading) return

    const userMsg: ChatMessage = { role: "user", content: message }
    const history = [...messages, userMsg]
      .filter(m => !m.transient && !m.error)
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }))

    setMessages(prev => [...prev, userMsg])
    setInput("")
    setLoading(true)

    try {
      const res = await api.post<ChatResponse>("/api/ai/chat", {
        message,
        sessionId: sessionIdRef.current,
        history,
      })
      const aiMsg: ChatMessage = {
        role: "assistant",
        content: extractText(res) || "I'm sorry, I couldn't generate a response. Please try again.",
        sources: extractSources(res),
        feedback: null,
      }
      setMessages(prev => [...prev, aiMsg])
    } catch (err) {
      let content = "Something went wrong. Please try again later."
      if (err instanceof ApiError && err.status === 429) {
        content = "You're sending messages too fast. Please wait a moment."
      } else if (err instanceof ApiError && err.message) {
        content = err.message
      }
      const errMsg: ChatMessage = {
        role: "assistant",
        content,
        sources: [],
        feedback: null,
        error: true,
        transient: err instanceof ApiError && err.status === 429,
      }
      setMessages(prev => [...prev, errMsg])
    } finally {
      setLoading(false)
    }
  }

  async function handleFeedback(idx: number, type: "positive" | "negative") {
    const msg = messages[idx]
    if (!msg || msg.feedback) return
    setMessages(prev =>
      prev.map((m, i) => (i === idx ? { ...m, feedback: type } : m))
    )
    try {
      await api.post<FeedbackResponse>("/api/ai/feedback", {
        sessionId: sessionIdRef.current,
        message: msg.content,
        feedback: type,
      })
    } catch {
      // non-fatal — feedback state already updated locally
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  // Visible messages excludes transient error ones from the count of "empty"
  const hasConversation = messages.some(m => m.role === "user")
  const showSuggestions = !hasConversation && suggestions.length > 0

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={open ? "Close chat" : "Open YMS COE AI chat"}
        className={cn(
          "fixed bottom-5 right-5 z-50 w-14 h-14 rounded-full bg-brand-800 hover:bg-brand-900 text-white shadow-lg shadow-brand-900/30 flex items-center justify-center transition-all hover:scale-105",
          "ring-4 ring-brand-800/20 dark:ring-brand-700/30",
          !open && "animate-pulse"
        )}
      >
        {open ? <X className="w-6 h-6" /> : <Bot className="w-6 h-6" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gold-500 ring-2 ring-white dark:ring-ink-950 flex items-center justify-center">
            <Sparkles className="w-2.5 h-2.5 text-white" />
          </span>
        )}
      </button>

      {/* Chat panel */}
      {open && (
        <div
          role="dialog"
          aria-label="YMS COE AI Assistant"
          className={cn(
            "fixed z-50 bg-white dark:bg-ink-900 rounded-xl shadow-2xl border border-ink-200 dark:border-ink-800 flex flex-col overflow-hidden",
            "bottom-24 right-3 left-3 sm:left-auto sm:bottom-24 sm:right-5",
            "w-auto sm:w-[350px]",
            "max-h-[min(500px,calc(100vh-7rem))]"
          )}
          style={{ animation: "chatFadeIn 0.18s ease-out" }}
        >
          <style>{`
            @keyframes chatFadeIn {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes typingDot {
              0%, 60%, 100% { opacity: 0.25; transform: translateY(0); }
              30% { opacity: 1; transform: translateY(-3px); }
            }
          `}</style>

          {/* Header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-brand-800 dark:bg-brand-900 text-white">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                <Bot className="w-4.5 h-4.5" />
              </div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm leading-tight truncate">YMS COE Assistant</div>
                <div className="text-[10px] text-brand-200 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-300 inline-block" />
                  Online · AI-powered
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1.5 rounded-md hover:bg-white/15 transition-colors"
              aria-label="Close chat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto px-3 py-3 space-y-3 bg-ink-50 dark:bg-ink-950"
            style={{ scrollbarWidth: "thin" }}
          >
            {messages.map((m, i) => (
              <div key={i} className={cn("flex flex-col", m.role === "user" ? "items-end" : "items-start")}>
                {m.role === "assistant" && (
                  <div className="flex items-center gap-1.5 mb-1 px-1">
                    <Bot className="w-3 h-3 text-brand-600 dark:text-brand-400" />
                    <span className="text-[10px] font-medium text-ink-500 dark:text-ink-400">AI Assistant</span>
                  </div>
                )}
                <div
                  className={cn(
                    "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                    m.role === "user"
                      ? "bg-brand-800 dark:bg-brand-700 text-white rounded-br-sm"
                      : m.error
                        ? "bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-200 border border-red-200 dark:border-red-800 rounded-bl-sm"
                        : "bg-white dark:bg-ink-100 text-ink-900 dark:text-ink-100 border border-ink-200 dark:border-ink-700 rounded-bl-sm"
                  )}
                >
                  {renderContent(m.content)}
                  {m.sources && m.sources.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-ink-200/70 dark:border-ink-700/70">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400 mb-1 flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        Sources
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {m.sources.map((s, si) => (
                          <span
                            key={si}
                            className="inline-flex items-center gap-1 rounded-md bg-brand-100 dark:bg-brand-900/40 text-brand-800 dark:text-brand-300 px-1.5 py-0.5 text-[10px] font-medium"
                          >
                            {s.category && <span className="text-brand-600 dark:text-brand-400">{s.category}:</span>}
                            {s.url ? (
                              <a
                                href={s.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline-offset-2 hover:underline truncate max-w-[160px]"
                              >
                                {s.title || "Source"}
                              </a>
                            ) : (
                              <span className="truncate max-w-[160px]">{s.title || "Source"}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Feedback row (AI non-error messages only) */}
                {m.role === "assistant" && !m.error && !m.transient && i > 0 && (
                  <div className="flex items-center gap-1 mt-1 px-1">
                    <button
                      onClick={() => handleFeedback(i, "positive")}
                      disabled={!!m.feedback}
                      aria-label="Helpful"
                      className={cn(
                        "p-1 rounded transition-colors disabled:cursor-default",
                        m.feedback === "positive"
                          ? "text-brand-700 dark:text-brand-400 bg-brand-100 dark:bg-brand-900/40"
                          : "text-ink-400 hover:text-brand-700 dark:hover:text-brand-400 hover:bg-ink-100 dark:hover:bg-ink-800",
                        m.feedback && m.feedback !== "positive" && "opacity-40"
                      )}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleFeedback(i, "negative")}
                      disabled={!!m.feedback}
                      aria-label="Not helpful"
                      className={cn(
                        "p-1 rounded transition-colors disabled:cursor-default",
                        m.feedback === "negative"
                          ? "text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/40"
                          : "text-ink-400 hover:text-red-700 dark:hover:text-red-400 hover:bg-ink-100 dark:hover:bg-ink-800",
                        m.feedback && m.feedback !== "negative" && "opacity-40"
                      )}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                    {m.feedback && (
                      <span className="text-[10px] text-ink-500 dark:text-ink-400 ml-0.5">
                        Thanks for your feedback!
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex flex-col items-start">
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <Bot className="w-3 h-3 text-brand-600 dark:text-brand-400" />
                  <span className="text-[10px] font-medium text-ink-500 dark:text-ink-400">AI Assistant</span>
                </div>
                <div className="bg-white dark:bg-ink-100 border border-ink-200 dark:border-ink-700 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                  <span style={{ animation: "typingDot 1.2s infinite", animationDelay: "0s" }} className="w-1.5 h-1.5 rounded-full bg-ink-400 dark:bg-ink-500 inline-block" />
                  <span style={{ animation: "typingDot 1.2s infinite", animationDelay: "0.2s" }} className="w-1.5 h-1.5 rounded-full bg-ink-400 dark:bg-ink-500 inline-block" />
                  <span style={{ animation: "typingDot 1.2s infinite", animationDelay: "0.4s" }} className="w-1.5 h-1.5 rounded-full bg-ink-400 dark:bg-ink-500 inline-block" />
                </div>
              </div>
            )}

            {/* Suggestions chips (initial state) */}
            {showSuggestions && (
              <div className="pt-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-ink-500 dark:text-ink-400 mb-1.5 px-1">
                  Suggested questions
                </div>
                <div className="flex flex-col gap-1.5">
                  {suggestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => sendMessage(q)}
                      disabled={loading}
                      className="text-left text-sm px-3 py-2 rounded-lg bg-white dark:bg-ink-100 border border-ink-200 dark:border-ink-700 text-ink-800 dark:text-ink-200 hover:border-brand-400 dark:hover:border-brand-600 hover:bg-brand-50 dark:hover:bg-brand-900/30 transition-colors disabled:opacity-50"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t border-ink-200 dark:border-ink-800 bg-white dark:bg-ink-900 p-2.5">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message…"
                disabled={loading}
                className="flex-1 h-10 rounded-full border border-ink-300 dark:border-ink-700 bg-white dark:bg-ink-900 px-4 py-2 text-sm text-ink-900 dark:text-ink-100 placeholder:text-ink-400 dark:placeholder:text-ink-500 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent disabled:opacity-50"
              />
              <button
                onClick={() => sendMessage()}
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="w-10 h-10 shrink-0 rounded-full bg-brand-800 hover:bg-brand-900 text-white flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed dark:bg-brand-700 dark:hover:bg-brand-800"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
            <div className="mt-1.5 text-center text-[10px] text-ink-400 dark:text-ink-500">
              Powered by {SCHOOL.shortName} AI · For information only
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ChatWidget
